export interface TemplateStaticDefaults {
  salutation?: string;
  title?: string;
  cta?: string;
  description?: string;
  image?: string;
  backgroundColor?: string;
  containerColor?: string;
  secondaryBackgroundColor?: string;
  accentColor?: string;
  secondaryAccentColor?: string;
  headingTextColor?: string;
  bodyTextColor?: string;
  buttonTextColor?: string;
  /** CTA background - falls back to the resolved accent color when omitted */
  buttonColor?: string;
  // a fixed body color, for a template whose body isn't painted in its (themed) container
  bodyBackgroundColor?: string;
  complianceText?: string;
  supportText?: string;
  taglineText?: string;
  liveBadgeText?: string;
  startTimeLabel?: string;
  watchTimeLabel?: string;
  watchOnDemandBadgeText?: string;
  availableOnDemandText?: string;
  communityText?: string;
  footerText?: string;
  descriptionTemplate?: (eventTitle: string) => string;
}

const upcomingTownhallReminderTemplate = (eventTitle: string): string =>
  `Just a reminder that '${eventTitle}' is happening soon. We've got a lot of ground to cover and can't wait to see you there!`;

const recordingAvailableTemplate = (): string =>
  "We've uploaded the full presentation along with the Q&A session. Catch up on the conversation at your own pace.";

const year = new Date().getFullYear();

// the Footer's company-info placeholder, per the BP4 design - the same in every template
export const DEFAULT_FOOTER_TEXT =
  'COMPANY NAME\n123 Main Street Anytown, GA 55555 USA\nemail@companyname.com\n1-800-555-5555';

export const templateStaticDefaults: Record<string, TemplateStaticDefaults> = {
  capri: {
    salutation: "YOU'RE INVITED!",
    title: '<span>Save<br>Your Spot</span>',
    cta: 'Register Now',
    description:
      'Join us for a rare evening of dialogue and discovery as we unveil the new paradigms of digital spatial design.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template11.webp',
    backgroundColor: '#2E2E2E',
    containerColor: '#0D0D0D',
    accentColor: '#F4522E',
    secondaryAccentColor: '#8F8F8F',
    headingTextColor: '#FFFFFF',
    bodyTextColor: '#FFFFFF',
    buttonTextColor: '#0D0D0D',
    complianceText: `© ${year} ACME Digital Editions. All rights reserved. You are receiving this because you subscribed to our spatial design updates.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  astoria: {
    salutation: "You've been invited to",
    cta: 'REGISTER',
    description: "Here's some brief info about this event. Keep it short and sweet.",
    image: 'https://assets.brandlive.com/assets/images/emails/email_template6.webp',
    backgroundColor: '#F9F8F8',
    containerColor: '#8EE5FF',
    accentColor: '#FFA21F',
    secondaryAccentColor: '#A0A0A0',
    headingTextColor: '#0F0F0F',
    bodyTextColor: '#0F0F0F',
    buttonTextColor: '#0D0D0D',
    bodyBackgroundColor: '#FFFFFF',
    complianceText: `Questions? Contact us at support@example.io. Unsubscribe from future events here. © ${year} BrandTV Inc.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  sterling: {
    salutation: "You've been invited to:",
    cta: 'REGISTER NOW',
    description:
      'The next chapter of high-performance streaming starts here. Secure your front-row seat to the engineering event of the season.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template9.webp',
    backgroundColor: '#FFFFFF',
    containerColor: '#F1E1FD',
    secondaryBackgroundColor: '#C8E4FC',
    accentColor: '#DA4600',
    secondaryAccentColor: '#D6D5C9',
    headingTextColor: '#DA4600',
    bodyTextColor: '#DA4600',
    buttonTextColor: '#F1E1FD',
    complianceText: `© ${year} COBALT SYSTEMS INC. All rights reserved. Proprietary and confidential series.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  montclair: {
    salutation: 'TECHFLOW',
    title: 'Join Our Upcoming Townhall',
    cta: 'Reserve My Seat',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template12.webp',
    backgroundColor: '#ECEDEF',
    containerColor: '#FFFFFF',
    secondaryBackgroundColor: '#EBF4FF',
    accentColor: '#2563EB',
    secondaryAccentColor: '#889AB0',
    headingTextColor: '#111111',
    bodyTextColor: '#111111',
    buttonTextColor: '#FFFFFF',
    complianceText: `Questions? Contact us at support@example.io. Unsubscribe from future emails here. © ${year} TechFlow Systems Inc.`,
    descriptionTemplate: upcomingTownhallReminderTemplate,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  amalfi: {
    salutation: "<span>DON'T<br>MISS THIS.</span>",
    cta: 'REGISTER NOW',
    description:
      'The next chapter of high-performance streaming starts here. Secure your front-row seat to the engineering event of the season.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template5.webp',
    backgroundColor: '#E3E3DD',
    containerColor: '#D6D5C9',
    secondaryBackgroundColor: '#902923',
    accentColor: '#A22C29',
    headingTextColor: '#F6F5EA',
    bodyTextColor: '#0A100D',
    buttonColor: '#B9BAA3',
    complianceText: `© ${year} COBALT SYSTEMS INC. All rights reserved. Proprietary and confidential series.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  florence: {
    title: '<span>Your<br>Session<br>Is This<br>Week</span>',
    cta: 'Add To Calendar',
    description:
      'Technical requirement: ensure webkit compatibility before joining. The session will be recorded for internal audit only.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template8.webp',
    backgroundColor: '#CEEB69',
    containerColor: '#FFFFFF',
    accentColor: '#CEEB69',
    headingTextColor: '#111111',
    bodyTextColor: '#979797',
    buttonTextColor: '#CEEB69',
    buttonColor: '#111111',
    complianceText: `Questions? Contact us at support@example.io. Unsubscribe from future emails here.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  chateau: {
    cta: 'Add to Calendar',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template10.webp',
    backgroundColor: '#FDF4F4',
    containerColor: '#FFFFFF',
    accentColor: '#F4A7A7',
    headingTextColor: '#111111',
    bodyTextColor: '#888888',
    buttonTextColor: '#1F1F1F',
    taglineText: `MUJI-STYLE COMMUNICATION • ${year} ESSENTIAL UPDATES ONLY`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  laurent: {
    title: 'Join Our Next Townhall',
    cta: 'Save My Seat',
    description: 'Join Our Upcoming Health Insights Townhall',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template4.webp',
    backgroundColor: '#FFFFFF',
    containerColor: '#F5F5F5',
    accentColor: '#00BFA5',
    headingTextColor: '#111111',
    bodyTextColor: '#888888',
    buttonTextColor: '#FFFFFF',
    // supportText:
    //   'If you have trouble joining, contact support for assistance. You can unsubscribe from future reminders at any time.',
    complianceText: `© ${year} ACME SYSTEMS INC.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  palais: {
    salutation: 'SPEAKER SERIES',
    title: 'Your Session Is Coming Up',
    cta: 'Join Now',
    description:
      'The seminar will examine the historical roots of Enlightenment thinking and its application to 21st-century governance. Attendance is strictly limited to registered fellows.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template2.webp',
    backgroundColor: '#F1EADF',
    containerColor: '#FBF6EE',
    accentColor: '#C9A84C',
    headingTextColor: '#1B1B1B',
    bodyTextColor: '#555555',
    buttonTextColor: '#C9A84C',
    buttonColor: '#1B1B1B',
    taglineText: 'ESTABLISHED 1894 · THE QUARTERLY GAZETTE',
    footerText: DEFAULT_FOOTER_TEXT,
  },
  aurelia: {
    salutation: "<span>WE'RE<br>GOING<br>LIVE!</span>",
    cta: 'JOIN THE STREAM',
    description:
      'The wait is over. The signal is live. Join thousands of creators as we push the boundaries of digital expression in real-time.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template13.webp',
    backgroundColor: '#2F2530',
    containerColor: '#FF0080',
    accentColor: '#FF0080',
    secondaryAccentColor: '#FFE500',
    headingTextColor: '#FFFFFF',
    bodyTextColor: '#666666',
    buttonTextColor: '#FFE500',
    buttonColor: '#000000',
    liveBadgeText: 'LIVE NOW',
    startTimeLabel: 'Start Time',
    complianceText: `© ${year} ACME. You are receiving this because you opted into our high-frequency updates. No refunds on digital vibes. Unsubscribe to kill the signal.`,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  viceroy: {
    title: '<span>Your<br>Session Is Coming Up</span>',
    cta: 'Join The Stream',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template7.webp',
    backgroundColor: '#F6F5EA',
    containerColor: '#585123',
    secondaryBackgroundColor: '#17181C',
    accentColor: '#585123',
    secondaryAccentColor: '#FFE500',
    headingTextColor: '#F6F5EA',
    bodyTextColor: '#0A100D',
    buttonTextColor: '#585123',
    buttonColor: '#F6F5EA',
    liveBadgeText: 'LIVE NOW',
    startTimeLabel: 'Start Time',
    complianceText: `© ${year} ACME Digital Editions. All rights reserved. You are receiving this because you subscribed to our spatial design updates.`,
    descriptionTemplate: upcomingTownhallReminderTemplate,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  carlton: {
    salutation: 'Missed the live session? No problem.',
    title: 'The Recording Is Ready',
    cta: 'Watch the Recording',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template3.webp',
    backgroundColor: '#FAFAFA',
    containerColor: '#FFFFFF',
    secondaryBackgroundColor: '#F4F8F4',
    accentColor: '#1B3D2F',
    headingTextColor: '#FFFFFF',
    bodyTextColor: '#666666',
    buttonTextColor: '#1B3D2F',
    buttonColor: '#FFFFFF',
    taglineText: 'WILDER',
    watchTimeLabel: 'Watch Time',
    watchOnDemandBadgeText: 'Watch On-Demand',
    availableOnDemandText: 'Topics: Soil Health, Reforestation, Bio-Ethics',
    communityText:
      'You are part of our community of 45,000+ environmental stewards. Our mission is to document the quiet resilience of our planet.',
    descriptionTemplate: recordingAvailableTemplate,
    footerText: DEFAULT_FOOTER_TEXT,
  },
  siena: {
    title: 'The Recording Is Now Available',
    cta: 'Watch the Recording',
    description:
      'The seminar examines the historical roots of Enlightenment thinking and its application to modern governance. Access is limited.',
    image: 'https://assets.brandlive.com/assets/images/emails/email_template1.webp',
    backgroundColor: '#FFFFFF',
    containerColor: '#FFF9F0',
    accentColor: '#6B1A1A',
    secondaryAccentColor: '#D4A847',
    headingTextColor: '#6B1A1A',
    bodyTextColor: '#666666',
    buttonTextColor: '#D4A847',
    buttonColor: '#6B1A1A',
    taglineText: 'DIGITAL EDITIONS · MCMXCIV',
    watchTimeLabel: 'Watch Time',
    footerText: DEFAULT_FOOTER_TEXT,
  },
};
